/**
 * A button for voting up programs.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {PermissionsInfo} from "@khan/shared-core";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {defaultApi as discussionsApi} from "#discussion-package/components/api.ts";

type Props = {
    scratchpadKey: string;
    upVoted: boolean;
    sumVotesIncremented: number;
} & WithApolloProps;
type State = {
    errorMessage: string;
    openModal: boolean;
    justVoted: boolean;
    sendingVote: boolean;
};

/* Upvote-only button used by scratchpads. */
class VoteControls extends React.Component<Props, State> {
    state: State = {
        errorMessage: "",
        sendingVote: false,
        openModal: false,
        justVoted: false,
    };

    registerVote: () => void = () => {
        const loginError = t`Log in first!`;

        if (!PermissionsInfo.isLoggedIn) {
            return this.setState({
                errorMessage: loginError,
                openModal: true,
            });
        }

        this.setState({sendingVote: true});
        discussionsApi
            .upvotePost(
                {
                    postKey: this.props.scratchpadKey,
                    alreadyVoted: this.props.upVoted || this.state.justVoted,
                },
                this.props.gqlClient,
            )
            .then((errorMessage) => {
                if (errorMessage) {
                    this.setState({
                        errorMessage,
                        openModal: true,
                        sendingVote: false,
                    });
                } else {
                    this.setState({justVoted: true, sendingVote: false});
                }
            })
            .catch((err) => {
                this.setState({
                    errorMessage: err,
                    openModal: true,
                    sendingVote: false,
                });
            });
    };

    handleClose: () => void = () => {
        this.setState({openModal: false});
    };

    render(): React.ReactNode {
        const upVoted = this.props.upVoted || this.state.justVoted;
        let votes = this.props.sumVotesIncremented;
        if (this.state.justVoted) {
            votes++;
        }
        const voteText = upVoted ? t`Voted Up!` : t`Vote Up`;
        const label = `${voteText} • ${votes}`;
        return (
            <React.Fragment>
                <Button
                    kind="primary"
                    onClick={this.registerVote}
                    spinner={this.state.sendingVote}
                    disabled={upVoted}
                >
                    {label}
                </Button>
                <ModalLauncher
                    onClose={this.handleClose}
                    opened={this.state.openModal}
                    modal={({closeModal}) => (
                        <OnePaneDialog
                            title={t`Oh noes!`}
                            content={<Body>{this.state.errorMessage}</Body>}
                            footer={
                                <View>
                                    <Button
                                        kind="primary"
                                        onClick={this.handleClose}
                                    >
                                        {t`Okay`}
                                    </Button>
                                </View>
                            }
                            style={styles.errorModal}
                        />
                    )}
                />
            </React.Fragment>
        );
    }
}

const styles = StyleSheet.create({
    errorModal: {
        maxHeight: 250,
    },
});

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof VoteControls,
        React.ComponentProps<typeof VoteControls>
    >
>;

export default compose(withApollo)(
    VoteControls,
) as React.ComponentType<ExportProps>;
